import { useCallback, useMemo, useState } from 'react';
import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';

import { resolveAllowedTruckIds } from './vehicleFilter';

/** 合成エントリ「すべて表示する」の id */
const ALL_GROUPS_ID = 0;

/** チェックされている index を group id に直す。一覧に無い index は捨てる */
export function groupIdsFromIndices(groupsWithAll: PlanningsGroupEntity[], indices: number[]): number[] {
  return indices.map((i) => groupsWithAll[i]?.id).filter((id): id is number => id != null);
}

/** group id を現在の一覧での index に直す。消えた / まだ届いていない id は捨てる */
export function indicesFromGroupIds(groupsWithAll: PlanningsGroupEntity[], ids: number[]): number[] {
  const indexById = new Map(groupsWithAll.map((g, i) => [g.id, i]));
  return ids.map((id) => indexById.get(id)).filter((i): i is number => i != null);
}

/**
 * 車両グループの絞り込み選択。
 *
 * 選択は **group id** で覚える。deliveries は 1 ページずつ届き、届くたびにグループ一覧
 * （表示中の車両を持つグループだけに絞った一覧）が増減するため、index で覚えると指す先が
 * ずれて別のグループのチェックが付いたり外れたりする。id なら一覧が変わってもずれないので、
 * 読み込みが終わるのを待たずに一覧を出せる。
 *
 * UI（TruckDisplayStatusMenu）は index で受け渡すので、境界でここが変換する。
 *
 * Why not localStorage に持ち越す: 全部のチェックを外した状態まで永続化され、次に開いたとき
 * 車両が 1 台も出ない画面から始まってしまう。グループ一覧は対象日の車両で変わるため、
 * 前日の選択を復元しても指す先がずれる。
 */
export function useTruckGroupSelection(groupsWithAll: PlanningsGroupEntity[]) {
  // null は「まだ触っていない」。グループ一覧は非同期で届くので初期値を静的には決められない
  const [touchedGroupIds, setTouchedGroupIds] = useState<number[] | null>(null);

  const selectedIndices = useMemo(() => {
    if (touchedGroupIds === null) return groupsWithAll.map((_g, i) => i);
    const indices = indicesFromGroupIds(groupsWithAll, touchedGroupIds);
    // 選んでいたグループが削除されて全滅したときは操作不能になるので「すべて表示する」へ戻す。
    // 利用者が自分で全部外したとき (touchedGroupIds が空) はそのまま空にする
    if (indices.length === 0 && touchedGroupIds.length > 0 && groupsWithAll.length > 0) {
      return [0];
    }
    return indices;
  }, [groupsWithAll, touchedGroupIds]);

  const setSelectedIndices = useCallback((next: number[]) => {
    setTouchedGroupIds(groupIdsFromIndices(groupsWithAll, next));
  }, [groupsWithAll]);

  const allowedTruckIds = useMemo(
    () => resolveAllowedTruckIds(groupsWithAll, selectedIndices),
    [groupsWithAll, selectedIndices],
  );

  /** 履歴に載せる実グループ（「すべて表示する」は実体ではないので除く） */
  const selectedGroups = useMemo(
    () => (allowedTruckIds === null
      ? []
      : selectedIndices.flatMap((i) => {
        const group = groupsWithAll[i];
        return group && group.id !== ALL_GROUPS_ID ? [group] : [];
      })),
    [allowedTruckIds, selectedIndices, groupsWithAll],
  );

  return { selectedIndices, setSelectedIndices, allowedTruckIds, selectedGroups };
}
